import { QueryClient } from "@tanstack/react-query";
import { createHttpClient } from "@toggl/sync/http";
import { HttpClientProvider as KyHttpClientProvider } from "@toggl/sync/http/provider";
import { useMemo } from "react";

import config from "@/config";
import { useFlashMessages } from "../flash-messages";

interface HttpClientProviderProps {
  children: React.ReactNode;
  queryClient: QueryClient;
}

export function HttpClientProvider({
  children,
  queryClient,
}: HttpClientProviderProps) {
  const { addMessage } = useFlashMessages();

  const httpClient = useMemo(
    () =>
      createHttpClient({
        base: `${config.apiUrl}/api/v3`,
        maxRetries: 0,
      }).extend({
        hooks: {
          beforeError: [
            (error) => {
              if (!error.response) {
                addMessage({
                  type: "network_error",
                });
              }

              return error;
            },
          ],
          afterResponse: [
            (request, _options, response) => {
              const isMeRequest = request.url.includes("/me");
              const isCareersRequest = request.url.includes("/careers/");

              if (response.status === 401 && !isMeRequest) {
                // Reset React Query queries on 401 errors to show the login page to the candidate (except for /me requests)
                queryClient.resetQueries();
                return response;
              }

              if (
                response.status >= 400 &&
                !(response.status === 401 && isMeRequest) &&
                !(response.status === 404 && isCareersRequest)
              ) {
                addMessage({
                  type: "api_error",
                  error: response.statusText,
                  errorId: Object.fromEntries(response.headers.entries())[
                    "x-request-id"
                  ],
                });
              }
            },
          ],
        },
      }),
    [addMessage, queryClient],
  );

  return (
    <KyHttpClientProvider client={httpClient}>{children}</KyHttpClientProvider>
  );
}
